import React, { useState, useCallback } from 'react';
import { useQuery } from 'react-query';
import { photosApi } from '../services/api';
import { PhotoQuery } from '../types';
import Filters from '../components/Filters';
import PhotoGrid from '../components/PhotoGrid';
import Pagination from '../components/Pagination';

const PhotoGallery: React.FC = () => {
  const [filters, setFilters] = useState<PhotoQuery>({
    page: 1,
    limit: 20
  });

  const {
    data: photoData,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['photos', filters],
    () => photosApi.getPhotos(filters),
    {
      keepPreviousData: true,
      staleTime: 30000, // 30 seconds
      refetchOnWindowFocus: false,
      retry: 2,
    }
  );

  const handleFiltersChange = useCallback((newFilters: PhotoQuery) => {
    setFilters(newFilters);
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setFilters(prevFilters => ({
      ...prevFilters,
      page
    }));
    // Scroll to top when page changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 mb-4">
          <svg className="h-12 w-12 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Ошибка загрузки</h3>
          <p className="text-gray-500 mb-4">Не удалось загрузить фотографии</p>
          <button
            onClick={() => refetch()}
            className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md transition-colors"
          >
            Попробовать снова
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filters */}
      <Filters
        onFiltersChange={handleFiltersChange}
        initialFilters={filters}
      />

      {/* Results Summary */}
      {photoData && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-600">
            {photoData.pagination.total > 0 ? (
              <>
                Найдено <span className="font-medium">{photoData.pagination.total}</span> фотографий
              </>
            ) : (
              'Фотографии не найдены'
            )}
          </div>
          <div className="text-sm text-gray-500">
            Страница {photoData.pagination.page} из {photoData.pagination.totalPages}
          </div>
        </div>
      )}

      {/* Photo Grid */}
      <PhotoGrid
        photos={photoData?.photos || []}
        loading={isLoading}
      />

      {/* Pagination */}
      {photoData && photoData.pagination.totalPages > 1 && (
        <Pagination
          currentPage={photoData.pagination.page}
          totalPages={photoData.pagination.totalPages}
          onPageChange={handlePageChange}
          hasNext={photoData.pagination.hasNext}
          hasPrev={photoData.pagination.hasPrev}
          total={photoData.pagination.total}
          limit={photoData.pagination.limit}
        />
      )}

      {/* Loading more indicator */}
      {isLoading && photoData && (
        <div className="text-center py-4">
          <div className="inline-flex items-center text-sm text-gray-500">
            <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Загрузка...
          </div>
        </div>
      )}
    </div>
  );
};

export default PhotoGallery;